import { firstStringInArray } from "@lending/utils/apiError";
import type { FieldPath, FieldValues, UseFormSetError } from "react-hook-form";

interface ApplicantDetailsFieldError {
  name: string;
  message: string;
}

const TEXT_FIELDS = ["first_name", "last_name", "email", "phone"];
const ADDRESS_FIELDS = ["residential_address", "mailing_address"];
const ADDRESS_INPUTS = ["street_address", "street_address_2", "city", "region_code", "postal_code"];

/**
 * Maps an applicant-details PUT error body (`{field: [message]}`) onto the edit drawer's form paths.
 * An error on a whole address, like the FI's combined street + apt character limit, goes on its
 * street address input. Anything that doesn't match a drawer input is left out.
 */
function getApplicantDetailsFieldErrors(error: unknown): ApplicantDetailsFieldError[] {
  if (!error || typeof error !== "object") return [];
  const body = error as Record<string, unknown>;
  const fieldErrors: ApplicantDetailsFieldError[] = [];

  TEXT_FIELDS.forEach((field) => {
    const message = firstStringInArray(body[field]);
    if (message) fieldErrors.push({ name: field, message });
  });

  // The combined first + last name limit is the serializer's only non-field error.
  const nonFieldMessage = firstStringInArray(body.non_field_errors);
  if (nonFieldMessage) fieldErrors.push({ name: "first_name", message: nonFieldMessage });

  ADDRESS_FIELDS.forEach((field) => {
    const value = body[field];
    const message = firstStringInArray(value);
    if (message) {
      fieldErrors.push({ name: `${field}.street_address`, message });
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, inputValue]) => {
        const inputMessage = firstStringInArray(inputValue);
        // AddressSerializer's own checks (line 1 limit, P.O. box) come back as nested non_field_errors.
        const input = key === "non_field_errors" ? "street_address" : key;
        if (ADDRESS_INPUTS.includes(input) && inputMessage) {
          fieldErrors.push({ name: `${field}.${input}`, message: inputMessage });
        }
      });
    }
  });

  return fieldErrors;
}

function applyFieldErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  fieldErrors: ApplicantDetailsFieldError[]
) {
  fieldErrors.forEach(({ name, message }) => setError(name as FieldPath<T>, { message }));
}

export { applyFieldErrors, getApplicantDetailsFieldErrors };
export type { ApplicantDetailsFieldError };
